import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import * as _ from 'lodash';
import { transparentize } from 'polished';
import {
	Component,
	createRef,
	forwardRef,
	JSXElementConstructor,
	ReactElement,
	Ref,
	RefObject,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { ThemeProvider } from 'styled-components';

import {
	AppBottomNavigation,
	AppHeaderNavigation,
	AppNavigator,
	ContinueButtonSync,
	getAppStateViews,
	ValidationDisabledSync,
} from './app/';
import ButtonConfiguration from './app/ButtonConfiguration';
import ElementConfiguration from './app/ElementConfiguration';
import AppConfiguration from './AppConfiguration';
import { AppProps } from './AppProps';
import AppService from './AppService';
import { AppState } from './AppState';
import { DeviceType } from './DeviceType';
import {
	ControlConfiguration,
	EasyClaimAppState,
	EasyClaimCaseResponseDto,
	EasyClaimDocumentDto,
	EasyClaimDocumentType,
	EasyClaimFrontendStep,
	VehicleDto,
} from './easyclaim.api.client.typescript/src/index';
import AutoMoveNextFeature from './easyclaim.app.components/AutoMoveNextFeature';
import { AdditionalHeaderContent } from './easyclaim.app.components/common/AdditionalHeaderContent';
import { UpdatedHeader } from './easyclaim.app.components/common/UpdatedHeader';
import { FullscreenPrompt } from './easyclaim.app.components/FullscreenPrompt';
import {
	AdditionalDamageWarningAlertComponent,
	ExampleModal,
	ExampleModalImage,
	HelpModal,
	ImageRecognitionMismatchModal,
	IsTestCaseModal,
	Loader,
	Main,
	MainError,
	MainLoader,
	MainMenu,
	Steps,
	TestCaseWarningAlertComponent,
	TutorialModal,
} from './easyclaim.app.components/index';
import { ChooseLangModal } from './easyclaim.app.components/modals/ChooseLangModal';
import { CompleteModal, CompleteModalItem } from './easyclaim.app.components/modals/CompleteModal';
import MileageModal from './easyclaim.app.components/modals/MileageModal';
import ApiErrorModal from './easyclaim.app.components/modals/ServiceErrorModal';
import Div100vh from './easyclaim.app.components/react-100vh-div';
import { StepsProgressBar } from './easyclaim.app.components/StepsProgressBar';
import { CustomerConfigurationProvider } from './easyclaim.app.contexts/CustomerConfigurationContext';
import { DocumentServiceProvider } from './easyclaim.app.contexts/DocumentServiceContext';
import { imagePersister, shouldDehydrateQuery } from './easyclaim.app.contexts/ImageQueryPersister';
import { ValidationProvider } from './easyclaim.app.contexts/ValidationContext';
import { IntlProvider } from './easyclaim.app.internationalization/index';
import { reportError } from './easyclaim.app.libs/reportError';
import { reportWarning } from './easyclaim.app.libs/reportWarning';
import { BlockedByDevice } from './easyclaim.app.screens';
import { BlockedDeviceSolvd } from './easyclaim.app.screens/BlockedDeviceSolvd';
import { Image, ImageSelectionService } from './easyclaim.app.services/ImageSelectionService';
import {
	EasyClaimDocumentService,
	EasyClaimService,
	MemoryMeasurementService,
} from './easyclaim.app.services/index';
import { CaseProgress, useAppProgressStore } from './easyclaim.app.stores/useAppProgressStore';
import theme from './easyclaim.app.theme/theme';
import { SelectNumbersFormat } from './numbroSelector';
import './polyfills';

const MEMORY_MEASUREMENT_INTERVAL_MS = 30000;

/** AppState keys whose changes should be persisted to {@link useAppProgressStore}. */
const PROGRESS_TRACKED_KEYS: (keyof AppState)[] = [
	'screen',
	'reviewReturnScreen',
	'isDashboardMileageSkipped',
	'isAdditionalDocumentsSkipped',
	'isVinPictureSkipped',
	'isRegistrationDocumentSkipped',
	'isVehicleInformationSkipped',
	'isDamageImagesSkipped',
	'damageImagesToggle',
	'isVehicleImagesSkipped',
	'isDamageDescriptionSkipped',
	'isDashboardMileageTextInputCompleted',
	'isOdometerAndMileageScreenCompleated',
	'isTermsAccepted',
	'cjQuestions',
	'cjQuestionsModified',
];

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: Infinity,
			retry: 1,
		},
	},
});

declare global {
	interface Window {
		__TANSTACK_QUERY_CLIENT__: import('@tanstack/query-core').QueryClient;
	}
}

window.__TANSTACK_QUERY_CLIENT__ = queryClient;

function isLoadedImage(val: unknown): val is Image {
	return !!(val as Image)?.document?.fileContent;
}

function isShellImage(val: unknown): boolean {
	const img = val as Image;

	return !!img?.document?.documentId && !img?.document?.fileContent;
}

/**
 * Merges full AppState snapshots without allowing image updates built from stale
 * props to overwrite loaded images or App-owned navigation state.
 */
export function mergeAppState(prev: AppState, next: AppState): AppState {
	const result = { ...next, isExecutingAction: prev.isExecutingAction };
	const isStaleNavigationSnapshot = !prev.isExecutingAction && next.isExecutingAction;

	if (isStaleNavigationSnapshot) {
		result.screen = prev.screen;
		result.reviewReturnScreen = prev.reviewReturnScreen;
	}

	for (const key of Object.keys(prev) as (keyof AppState)[]) {
		const prevVal = prev[key];
		const nextVal = next[key];

		if (
			isLoadedImage(prevVal) &&
			isShellImage(nextVal) &&
			prevVal.document?.documentId === (nextVal as Image).document?.documentId
		) {
			(result as Record<string, unknown>)[key] = prevVal;
		} else if (Array.isArray(prevVal) && Array.isArray(nextVal)) {
			(result as Record<string, unknown>)[key] = nextVal.map((nextImg: unknown, i: number) => {
				const prevImg = prevVal[i];

				if (
					isLoadedImage(prevImg) &&
					isShellImage(nextImg) &&
					prevImg.document?.documentId === (nextImg as Image).document?.documentId
				) {
					return prevImg;
				}

				return nextImg;
			});
		}
	}

	return result;
}

/**
 * Decides which screen the customer journey should resume on when a case is
 * (re)loaded. Prefers the locally persisted screen over the backend's screen
 * whenever it represents further progress (e.g. the customer advanced past a
 * step that the backend does not track), but never resumes on a screen that
 * is no longer part of the case's current step configuration or that is
 * behind the backend's own screen.
 */
function resolveResumeScreen(
	backendScreen: EasyClaimAppState | undefined,
	storedScreen: EasyClaimAppState | undefined,
	stepOrder: (EasyClaimAppState | undefined)[] | undefined
): EasyClaimAppState | undefined {
	if (!storedScreen || !stepOrder || !stepOrder.length) {
		return backendScreen;
	}

	const backendIndex = backendScreen ? stepOrder.indexOf(backendScreen) : -1;
	const storedIndex = stepOrder.indexOf(storedScreen);

	if (storedIndex === -1) {
		return backendScreen;
	}

	if (backendIndex === -1) {
		return storedScreen;
	}

	return storedIndex > backendIndex ? storedScreen : backendScreen;
}

export default class App extends Component<AppProps, AppState> {
	private appNavigator: AppNavigator;
	private appScrollController: RefObject<HTMLDivElement>;

	constructor(props: AppProps) {
		super(props);
		this.appScrollController = createRef<HTMLDivElement>();
		this.appNavigator = new AppNavigator(this, this.scrollToTop);

		const easyClaimService = new EasyClaimService(
			props.appConfiguration.easyClaimBackend.basePath,
			props.appConfiguration.easyClaimBackend.apiKey,
			this.props.appParameters
		);

		const documentService = new EasyClaimDocumentService(
			props.appConfiguration.easyClaimBackend.basePath,
			props.appConfiguration.easyClaimBackend.apiKey
		);

		const appStateViews = getAppStateViews({
			isBackButtonVisibleDelegate: this.appNavigator.canGoBack,
		});

		this.state = {
			showFullscreenPrompt: false,
			easyClaimService: easyClaimService,
			documentService: documentService,
			additionalDamageImages: [],
			additionalDocumentImages: [],
			appParameters: this.props.appParameters,
			appService: new AppService(this),
			isHelpOpen: false,
			isMainMenuOpen: false,
			isTutorialOpen: false,
			isExecutingAction: false,
			serviceErrors: [],
			exampleModalOpen: false,
			exampleModalImages: [],
			exampleModalHeaderTitleId: 'imageRecognitionFeedback2.exemplaryPhoto',
			exampleModalHeaderCloseId: 'button.close',
			exampleModalName: null,
			completeModalOpen: false,
			completeModalItems: [],
			completeModalNextButtonVisible: false,
			completeModalHeaderTitleId: 'TODO',
			completeModalDescriptionTitleId: 'TODO',
			completeModalOkButtonTitleId: 'button.ok',
			completeModalNextButtonTitleId: 'button.addLater',
			mismatchModalOpen: false,
			mismatchModalImages: [],
			theme,
			setAppState: (newAppState: AppState) => {
				return new Promise<void>((resolve) => {
					this.setState((prev) => mergeAppState(prev, newAppState), resolve);
				});
			},
			onNext: this.handleOnNext,
			onPrev: this.appNavigator.onPrev,
			onTutorial: this.showTutorial,
			showExampleModal: this.showExampleModal,
			hideExampleModal: this.hideExampleModal,
			showCompleteModal: this.showCompleteModal,
			hideCompleteModal: this.hideCompleteModal,
			showMismatchModal: this.showMismatchModal,
			hideMismatchModal: this.hideMismatchModal,
			isAdditionalDocumentsSkipped: false,
			isRegistrationDocumentSkipped: false,
			isVehicleInformationSkipped: false,
			isVinPictureSkipped: false,
			isVehicleImagesSkipped: false,
			isDamageDescriptionSkipped: false,
			isDamageImagesSkipped: false,
			damageImagesToggle: undefined,
			appStateViews: appStateViews,
			isDashboardMileageSkipped: false,
			getFileUrl: this.getFileUrl,
			isDashboardMileageTextInputCompleted: false,
			isOdometerAndMileageScreenCompleated: false,
			isLangModalOpen: false,
			isMileageMismatchModalOpen: false,
			isMileageMismatchModalConfirmValue: false,
			inputError: false,
			isTermsAccepted: false,
			cjQuestions: [],
			cjQuestionsModified: false,
		};
	}

	private memoryMeasurementService = new MemoryMeasurementService();
	private memoryMeasurementIntervalId: ReturnType<typeof setInterval> | undefined;

	componentDidMount() {
		this.runAsync(this.initializeAsync);
	}

	private initializeAsync = async (): Promise<void> => {
		if (typeof performance !== 'undefined') {
			performance.setResourceTimingBufferSize?.(150);
			performance.addEventListener?.('resourcetimingbufferfull', () => {
				performance.clearResourceTimings();
			});
		}

		void this.memoryMeasurementService.measureAndReport(this.state.screen);
		this.memoryMeasurementIntervalId = setInterval(() => {
			void this.memoryMeasurementService.measureAndReport(this.state.screen);
		}, MEMORY_MEASUREMENT_INTERVAL_MS);

		if (navigator.mediaDevices?.getUserMedia) {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });

				stream.getTracks().forEach((t) => t.stop());
			} catch (e) {
				// Camera permission denied or device unavailable — non-fatal, continue
				reportWarning('Camera permission denied or device unavailable', { error: e });
				reportWarning('Camera permission denied or device unavailable', { error: e });
			}
		}

		await new Promise<void>((resolve) => {
			if (!navigator.geolocation) {
				resolve();

				return;
			}
			navigator.geolocation.getCurrentPosition(
				() => resolve(),
				() => resolve(),
				{ timeout: 10000, maximumAge: 60000 }
			);
		});
	};

	componentWillUnmount() {
		if (this.memoryMeasurementIntervalId !== undefined) {
			clearInterval(this.memoryMeasurementIntervalId);
		}
	}

	/**
	 * Persists the client-side customer journey progress (current screen and
	 * related flags/answers) to the zustand-backed local storage so that a
	 * page reload resumes on the same step instead of restarting the journey.
	 */
	componentDidUpdate(_prevProps: AppProps, prevState: AppState) {
		const caseId = this.state.easyClaimCase?.caseId;

		if (!caseId) {
			return;
		}

		const hasProgressChanged = PROGRESS_TRACKED_KEYS.some(
			(key) => prevState[key] !== this.state[key]
		);

		if (!hasProgressChanged) {
			return;
		}

		useAppProgressStore.getState().setProgress(caseId, {
			screen: this.state.screen,
			reviewReturnScreen: this.state.reviewReturnScreen,
			isDashboardMileageSkipped: this.state.isDashboardMileageSkipped,
			isAdditionalDocumentsSkipped: this.state.isAdditionalDocumentsSkipped,
			isVinPictureSkipped: this.state.isVinPictureSkipped,
			isRegistrationDocumentSkipped: this.state.isRegistrationDocumentSkipped,
			isVehicleInformationSkipped: this.state.isVehicleInformationSkipped,
			isDamageImagesSkipped: this.state.isDamageImagesSkipped,
			damageImagesToggle: this.state.damageImagesToggle,
			isVehicleImagesSkipped: this.state.isVehicleImagesSkipped,
			isDamageDescriptionSkipped: this.state.isDamageDescriptionSkipped,
			isDashboardMileageTextInputCompleted: this.state.isDashboardMileageTextInputCompleted,
			isOdometerAndMileageScreenCompleated: this.state.isOdometerAndMileageScreenCompleated,
			isTermsAccepted: this.state.isTermsAccepted,
			cjQuestions: this.state.cjQuestions,
			cjQuestionsModified: this.state.cjQuestionsModified,
		});
	}

	private fullscreenPromptShown = false;

	private setStateAsync = <K extends keyof AppState>(state: Pick<AppState, K>): Promise<void> =>
		new Promise<void>((resolve) => {
			this.setState(state, resolve);
		});

	private runAsync = (operation: () => Promise<void>): void => {
		void operation().catch((error: unknown) => {
			reportError(error, { context: 'App.action' });
		});
	};

	private showFullscreenPromptOnce = () => {
		if (this.fullscreenPromptShown) return;
		this.fullscreenPromptShown = true;
		const el = document.documentElement as any;
		const isFullscreenSupported = !!(
			el.requestFullscreen ||
			el.webkitRequestFullscreen ||
			el.mozRequestFullScreen ||
			el.msRequestFullscreen
		);

		if (isFullscreenSupported) {
			this.setState({ showFullscreenPrompt: true });

			const caseId = this.state.easyClaimCase?.caseId;

			if (caseId) {
				useAppProgressStore.getState().setProgress(caseId, { fullscreenPromptShown: true });
			}
		}
	};

	private handleOnNext = async () => {
		await this.appNavigator.onNext();
		this.showFullscreenPromptOnce();
	};

	private scrollToTop = () => {
		this.appScrollController?.current?.scroll({ top: 0, left: 0, behavior: 'auto' });
	};

	private showExampleModal = async (
		images: ExampleModalImage[],
		headerTitleId: string | null = null,
		closeTitleId: string | null = null,
		name: string | null
	) => {
		const newState: AppState = {
			...this.state,
			exampleModalOpen: true,
			exampleModalImages: images,
			exampleModalHeaderTitleId: headerTitleId,
			exampleModalName: name,
			exampleModalHeaderCloseId: closeTitleId,
		};

		await this.setStateAsync(newState);
	};

	private hideExampleModal = async () => {
		const newState: AppState = {
			...this.state,
			exampleModalOpen: false,
			exampleModalImages: [],
		};

		await this.setStateAsync(newState);
	};

	private showCompleteModal = async (
		items: CompleteModalItem[],
		nextButtonVisible: boolean,
		headerTitleId: string,
		descriptionTitleId: string,
		okButtonTitleId: string | null,
		nextButtonTitleId: string | null
	) => {
		const isInputError: boolean = items.some(
			(x) => x.id === 'completeDashboardMileageModal.hasValidMileage'
		);
		const newState: AppState = {
			...this.state,
			completeModalOpen: true,
			completeModalItems: items,
			completeModalNextButtonVisible: nextButtonVisible,
			completeModalHeaderTitleId: headerTitleId,
			completeModalDescriptionTitleId: descriptionTitleId,
			completeModalOkButtonTitleId: okButtonTitleId,
			completeModalNextButtonTitleId: nextButtonTitleId,
			inputError: isInputError,
		};

		await this.setStateAsync(newState);
	};

	private enterFullscreen = () => {
		const el = document.documentElement as any;

		if (el.requestFullscreen) {
			el.requestFullscreen().catch((err: any) => {
				reportWarning('[Fullscreen] FAILED', {
					error: err,
					name: err.name,
					message: err.message,
				});
			});
		} else if (el.webkitRequestFullscreen) {
			el.webkitRequestFullscreen();
		} else if (el.mozRequestFullScreen) {
			el.mozRequestFullScreen();
		} else if (el.msRequestFullscreen) {
			el.msRequestFullscreen();
		}
		this.setState({ showFullscreenPrompt: false });
	};

	public render() {
		const { appService, theme, documentService, easyClaimService, appConfiguration } = this.state;
		const intlConfig = appService.getIntlConfig();

		return (
			<>
				<CustomerConfigurationProvider configuration={this.state.easyClaimCase?._configuration}>
					<IntlProvider
						{...intlConfig}
						onError={(e) => {
							/*SIlenced Intl error to avoid spamming entire console with missing translation error*/
						}}
					>
						<ThemeProvider theme={theme}>
							{!!this.state.easyClaimCase?._configuration?.useSolvdStyles &&
								this.state.easyClaimCase._configuration.isFullscreenPromptEnabled &&
								this.state.showFullscreenPrompt && (
									<FullscreenPrompt
										onEnter={this.enterFullscreen}
										onDismiss={() => this.setState({ showFullscreenPrompt: false })}
									/>
								)}
							<Loader
								loader={async () => {
									await this.loadCurrentAppState(
										appService,
										documentService,
										easyClaimService,
										appConfiguration
									);
								}}
								onLoad={<MainLoader message={<FormattedMessage id="loader.loading" />} />}
								onError={<MainError message={<FormattedMessage id="loader.error" />} />}
								onSuccess={this.renderAppContainerComponent()}
							/>
						</ThemeProvider>
					</IntlProvider>
				</CustomerConfigurationProvider>
			</>
		);
	}

	private renderAppContainerComponent(): ReactElement<any, string | JSXElementConstructor<any>> {
		const CurrentStateScreenRenderer = this.state.appService.getCurrentStateScreenRenderer();

		const CurrentScreen = this.state.isBlockedByWrongDevice
			? undefined
			: CurrentStateScreenRenderer?.appStateComponentRender(this.state);

		const isLogoVisible =
			!!this.state.easyClaimCase && !!this.appNavigator.getCurrentFrontendStep()?.isLogoVisible;

		const isStepsVisible = CurrentStateScreenRenderer?.isStepsActive?.(this.state) ?? false;

		const isProgressBarEnabled =
			this.state.easyClaimCase?._configuration?.isImageUploadProgressBarVisible;

		const isAdditionalHeaderContentVisible =
			CurrentStateScreenRenderer?.isAdditionalHeaderInfoActive?.(this.state) ?? false;

		const headerBackButtonConfiguration: ButtonConfiguration = {
			onClick: this.tryGoToPrevStep,
			isVisible: CurrentStateScreenRenderer?.isBackButtonVisible?.() ?? false,
			textId: 'button.prev',
		};

		const headerMainMenuButtonConfiguration: ButtonConfiguration = {
			onClick: this.showMainMenu,
			isVisible: !!this.state.easyClaimCase?._configuration?.isMenuVisible,
			textId: 'na',
		};

		const headerShowChangeLangModalButtonConfiguration: ButtonConfiguration = {
			onClick: this.showChangeLangModal,
			isVisible:
				this.state.screen === EasyClaimAppState.Landing ||
				this.state.screen === EasyClaimAppState.LandingApproval ||
				this.state.screen === EasyClaimAppState.Onboarding ||
				this.state.screen === EasyClaimAppState.OnboardingStart,
			textId: this.state.appParameters.lang,
		};

		const bottomBackButtonConfiguration: ButtonConfiguration = {
			onClick: this.tryGoToPrevStep,
			disabled: this.state.isImageLoading || this.state.isExecutingAction ? true : undefined,
			isVisible: CurrentStateScreenRenderer?.isBackButtonVisible?.() ?? false,
			textId: 'button.prev',
		};

		const bottomNextButtonConfiguration: ButtonConfiguration = {
			onClick: this.tryGoToNextStep,
			disabled:
				this.state.isImageLoading ||
				this.state.isExecutingAction ||
				CurrentStateScreenRenderer?.nextButtonDisabled?.(this.state) === true
					? true
					: undefined,
			isVisible: CurrentStateScreenRenderer?.isNextButtonVisible === true,
			textId: CurrentStateScreenRenderer?.nextButtonTextId
				? CurrentStateScreenRenderer.nextButtonTextId
				: 'button.next',
		};

		const bottomSkipButtonConfiguration: ButtonConfiguration = {
			onClick: this.skipCurrentStep,
			disabled: this.state.isImageLoading,
			isVisible:
				!!CurrentStateScreenRenderer &&
				!!CurrentStateScreenRenderer.onSkip &&
				!!CurrentStateScreenRenderer.canSkip &&
				CurrentStateScreenRenderer.canSkip(this.state) === true,
			textId: 'button.skip',
		};

		const bottomPoweredByConfiguration: ElementConfiguration = {
			isVisible:
				!!CurrentStateScreenRenderer &&
				CurrentStateScreenRenderer.appState === EasyClaimAppState.OnboardingStart,
			textId: 'poweredBy',
		};

		const useSolvdStyles = !!this.state.easyClaimCase?._configuration?.useSolvdStyles;

		return (
			<PersistQueryClientProvider
				client={queryClient}
				persistOptions={{
					persister: imagePersister,
					maxAge: 1000 * 60 * 60 * 24 * 7,
					dehydrateOptions: { shouldDehydrateQuery },
				}}
			>
				<DocumentServiceProvider documentService={this.state.documentService}>
					<ValidationProvider>
						<AppContainer className="app-container" ref={this.appScrollController}>
							{useSolvdStyles && (
								<UpdatedHeader
									caseId={this.state.easyClaimCase?.caseId}
									documentService={this.state.documentService}
									currentLang={this.state.appParameters.lang}
									localizedCaseUrls={this.state.easyClaimCase?.localizedCaseUrls}
									caseState={this.state.screen}
								/>
							)}
							{!useSolvdStyles && (
								<Header className={`header-${this.state.screen}`}>
									<AppHeaderNavigation
										isLogoVisible={isLogoVisible}
										caseId={this.state.easyClaimCase?.caseId}
										documentService={this.state.documentService}
										BackButtonConfiguration={headerBackButtonConfiguration}
										MenuButtonConfiguration={headerMainMenuButtonConfiguration}
										ShowChangeLangModalButtonConfiguration={
											headerShowChangeLangModalButtonConfiguration
										}
										EasyClaimCase={this.state.easyClaimCase}
									/>
									{isStepsVisible && <Steps {...this.state} />}
									{isProgressBarEnabled && isStepsVisible && <StepsProgressBar {...this.state} />}
									{isAdditionalHeaderContentVisible && (
										<AdditionalHeaderContent easyClaimAttributes={this.state.easyClaimCase} />
									)}
								</Header>
							)}
							{!this.state.isBlockedByWrongDevice && CurrentScreen && (
								<>
									<Main>
										{CurrentScreen}
										{CurrentStateScreenRenderer?.appState !== EasyClaimAppState.Landing && (
											<>
												<ContinueButtonSync config={bottomNextButtonConfiguration} />
												<ValidationDisabledSync />
												<AppBottomNavigation
													isExecutingAction={this.state.isExecutingAction}
													spinnerColor={this.state.theme.colors.text.primary}
													BackButtonConfiguration={bottomBackButtonConfiguration}
													SkipButtonConfiguration={bottomSkipButtonConfiguration}
													PoweredByConfiguration={bottomPoweredByConfiguration}
													CurrentScreen={this.state.screen!}
												/>
											</>
										)}
									</Main>
								</>
							)}
							{this.state?.easyClaimCase?._configuration?.isMenuVisible && (
								<MainMenu
									isOpen={this.state.isMainMenuOpen}
									showHelpButton={!!this.state?.easyClaimCase?._configuration?.showHelpButton}
									onClose={() => this.runAsync(this.hideMainMenu)}
									imprintText={this.state.easyClaimCase?._configuration?.imprint ?? undefined}
									imprintLink={this.state.easyClaimCase?._configuration?.imprintUrl ?? undefined}
									hotlineNumber={
										this.state.easyClaimCase?._configuration?.hotlineNumber ?? undefined
									}
									dataPrivacyText={
										this.state.easyClaimCase?._configuration?.termsAndConditions ?? undefined
									}
									dataPrivacyLink={
										this.state.easyClaimCase?._configuration?.dataPrivacyUrl ?? undefined
									}
									serviceTimes={this.state.easyClaimCase?._configuration?.serviceTimes ?? undefined}
									onHelpButtonClicked={() => this.runAsync(this.showHelpModal)}
								/>
							)}
							<AutoMoveNextFeature appNavigator={this.appNavigator} appState={this.state} />
							<IsTestCaseModal isOpen={this.state.appService.isItTestCase()} />
							<HelpModal
								isOpen={this.state.isHelpOpen}
								onClose={() => this.runAsync(this.hideHelpModal)}
								onTutorialClicked={() => this.runAsync(this.showTutorialModal)}
								showTutorial
								showCall
								phoneNumber={
									this.state.easyClaimCase &&
									this.state.easyClaimCase._configuration &&
									this.state.easyClaimCase._configuration.hotlineNumber!
								}
							/>
							<TutorialModal
								isOpen={this.state.isTutorialOpen}
								onClose={() => this.runAsync(this.hideTutorialModal)}
								easyClaimCase={this.state.easyClaimCase}
							/>
							<ApiErrorModal
								isOpen={!_.isEmpty(this.state.serviceErrors)}
								serviceError={_.last(this.state.serviceErrors)}
								onClose={() => this.runAsync(this.hideApiErrorModal)}
								useSolvdStyles={useSolvdStyles}
							/>
							<ExampleModal
								isOpen={this.state.exampleModalOpen}
								headerTitleId={this.state.exampleModalHeaderTitleId}
								closeTitleId={this.state.exampleModalHeaderCloseId}
								images={this.state.exampleModalImages}
								name={this.state.exampleModalName}
								onClose={() => this.runAsync(this.hideExampleModal)}
							/>
							<CompleteModal
								isOpen={this.state.completeModalOpen}
								nextButtonVisible={this.state.completeModalNextButtonVisible}
								headerTitleId={this.state.completeModalHeaderTitleId}
								descriptionTitleId={this.state.completeModalDescriptionTitleId}
								okButtonTitleId={this.state.completeModalOkButtonTitleId}
								nextButtonTitleId={this.state.completeModalNextButtonTitleId}
								items={this.state.completeModalItems}
								onOk={() => this.runAsync(this.hideCompleteModal)}
								onNext={() => this.runAsync(this.state.onNext)}
							/>
							{this.state.isMileageMismatchModalOpen &&
								!this.state.isMileageMismatchModalConfirmValue && (
									<MileageModal
										isOpen={this.state.isMileageMismatchModalOpen}
										insertedMileage={this.state.easyClaimCase?.vehicle?.userProvidedMileage ?? null}
										pictureMileage={
											this.state.appService.getDashboardImageMileageValue()?.toString() ?? ''
										}
										onConfirm={(mileage) => this.runAsync(() => this.confirmMileageModal(mileage))}
										onReverse={() => this.runAsync(this.reverseMileageModal)}
										onClose={(mileage) => this.runAsync(() => this.confirmMileageModal(mileage))}
										// onNext={(newEasyClaimCase) => this.setState({ isMileageMismatchModalConfirmValue: true, isMileageMismatchModalOpen: false, easyClaimCase: newEasyClaimCase })}
									/>
								)}
							{!this.state.easyClaimCase?._configuration?.showUpdatedVerificationResults &&
								!this.state.easyClaimCase?._configuration?.canScrollToMissingPhoto && (
									<ImageRecognitionMismatchModal
										isOpen={this.state.mismatchModalOpen}
										images={this.state.mismatchModalImages}
										onClose={() => this.runAsync(this.hideMismatchModal)}
										onNext={() => this.runAsync(this.state.onNext)}
										mileage={this.state.easyClaimCase?.vehicle?.userProvidedMileage ?? null}
										easyClaimCase={this.state.easyClaimCase!}
										screen={this.state.screen!}
									/>
								)}
							{!!this.state.easyClaimCase && (
								<ChooseLangModal
									caseResponse={this.state.easyClaimCase}
									isOpen={this.state.isLangModalOpen}
									currentLang={this.state.appParameters.lang}
									onClose={() => this.runAsync(this.hideChangeLangModal)}
								/>
							)}
							<TestCaseWarningAlertComponent showAlert={this.state.appService.isItTestCase()} />
							<AdditionalDamageWarningAlertComponent showAlert={this.additionalDamagesDetected()} />
							{this.state.isBlockedByWrongDevice &&
								(useSolvdStyles ? (
									<BlockedDeviceSolvd
										appState={this.state}
										caseId={this.state.easyClaimCase?.caseId}
										documentService={this.state.documentService}
									/>
								) : (
									<BlockedByDevice {...this.state} />
								))}
						</AppContainer>
					</ValidationProvider>
				</DocumentServiceProvider>
			</PersistQueryClientProvider>
		);
	}

	private async loadCurrentAppState(
		appService: AppService,
		documentService: EasyClaimDocumentService | undefined,
		easyClaimService: EasyClaimService | undefined,
		appConfiguration: AppConfiguration | undefined
	) {
		const easyClaimCase = await appService.loadEasyClaimCase();

		SelectNumbersFormat(easyClaimCase?._configuration!);

		if (
			documentService !== undefined &&
			easyClaimService !== undefined &&
			!!easyClaimCase &&
			!!easyClaimCase.caseId
		) {
			const caseId: string = easyClaimCase.caseId;
			const images: EasyClaimDocumentDto[] | null =
				easyClaimCase.documents !== undefined ? easyClaimCase.documents : null;

			let registrationDocument: Image | undefined;
			let registrationDocument2: Image | undefined;
			let vehicleImageFrontLeft: Image | undefined;
			let vehicleImageFrontRight: Image | undefined;
			let vehicleImageRearRight: Image | undefined;
			let vehicleImageRearLeft: Image | undefined;

			let vehicleImageFront: Image | undefined;
			let vehicleImageRear: Image | undefined;
			let vehicleImageLeft: Image | undefined;
			let vehicleImageRight: Image | undefined;

			let dashboardImage: Image | undefined;
			let vinImage: Image | undefined;
			let closeUpDamageImage: Image | undefined;
			let overallDamageImage: Image | undefined;
			let overallDashboardImage: Image | undefined;
			let windshieldImage: Image | undefined;
			const additionalDamageImages: Image[] = [];
			let additionalDocumentImage: Image | undefined;
			const additionalDocumentImages: Image[] = [];

			let vehicleDatasheetInside: Image | undefined;
			let vehicleDatasheetOutside: Image | undefined;
			let dniInside: Image | undefined;
			let dniOutside: Image | undefined;
			let driverLicenseInside: Image | undefined;
			let driverLicenseOutside: Image | undefined;
			let itv: Image | undefined;

			if (!!images && !!images.length) {
				let damageImageNo = 0;
				let additionalDocumentNo = 0;

				for (let index = 0; index < images.length; index++) {
					const image = images[index];
					const documentId = image.documentId;

					if (!!documentId && !!image) {
						if (image.documentType === EasyClaimDocumentType.RegistrationDocument) {
							registrationDocument = documentService.buildImageShell(
								caseId,
								image,
								'registrationDocument'
							);
						} else if (image.documentType === EasyClaimDocumentType.RegistrationDocumentInside) {
							registrationDocument2 = documentService.buildImageShell(
								caseId,
								image,
								'registrationDocument2'
							);
						} else if (image.documentType === EasyClaimDocumentType.FrontLeftPicture) {
							vehicleImageFrontLeft = documentService.buildImageShell(
								caseId,
								image,
								'vehicleFrontLeft'
							);
						} else if (image.documentType === EasyClaimDocumentType.RearRightPicture) {
							vehicleImageRearRight = documentService.buildImageShell(
								caseId,
								image,
								'vehicleRearRight'
							);
						} else if (image.documentType === EasyClaimDocumentType.FrontRightPicture) {
							vehicleImageFrontRight = documentService.buildImageShell(
								caseId,
								image,
								'vehicleFrontRight'
							);
						} else if (image.documentType === EasyClaimDocumentType.RearLeftPicture) {
							vehicleImageRearLeft = documentService.buildImageShell(
								caseId,
								image,
								'vehicleRearLeft'
							);
						} else if (image.documentType === EasyClaimDocumentType.DashboardPicture) {
							dashboardImage = documentService.buildImageShell(caseId, image, 'dashboardPicture');
						} else if (image.documentType === EasyClaimDocumentType.OverallDashboardPicture) {
							overallDashboardImage = documentService.buildImageShell(
								caseId,
								image,
								'overallDashboardImage'
							);
						} else if (image.documentType === EasyClaimDocumentType.WindshieldPicture) {
							windshieldImage = documentService.buildImageShell(caseId, image, 'windshieldImage');
						} else if (image.documentType === EasyClaimDocumentType.VinPicture) {
							vinImage = documentService.buildImageShell(caseId, image, 'vinPicture');
						} else if (image.documentType === EasyClaimDocumentType.FrontPerspectivePicture) {
							vehicleImageFront = documentService.buildImageShell(caseId, image, 'vehicleFront');
						} else if (image.documentType === EasyClaimDocumentType.RightPerspectivePicture) {
							vehicleImageRight = documentService.buildImageShell(caseId, image, 'vehicleRight');
						} else if (image.documentType === EasyClaimDocumentType.RearPerspectivePicture) {
							vehicleImageRear = documentService.buildImageShell(caseId, image, 'vehicleRear');
						} else if (image.documentType === EasyClaimDocumentType.LeftPerspectivePicture) {
							vehicleImageLeft = documentService.buildImageShell(caseId, image, 'vehicleLeft');
						} else if (image.documentType === EasyClaimDocumentType.VehicleDatasheetInside) {
							vehicleDatasheetInside = documentService.buildImageShell(
								caseId,
								image,
								'vehicleDatasheetInside'
							);
						} else if (image.documentType === EasyClaimDocumentType.VehicleDatasheetOutside) {
							vehicleDatasheetOutside = documentService.buildImageShell(
								caseId,
								image,
								'vehicleDatasheetOutside'
							);
						} else if (image.documentType === EasyClaimDocumentType.DniInside) {
							dniInside = documentService.buildImageShell(caseId, image, 'dniInside');
						} else if (image.documentType === EasyClaimDocumentType.DniOutside) {
							dniOutside = documentService.buildImageShell(caseId, image, 'dniOutside');
						} else if (image.documentType === EasyClaimDocumentType.DriverLicenseInside) {
							driverLicenseInside = documentService.buildImageShell(
								caseId,
								image,
								'driverLicenseInside'
							);
						} else if (image.documentType === EasyClaimDocumentType.DriverLicenseOutside) {
							driverLicenseOutside = documentService.buildImageShell(
								caseId,
								image,
								'driverLicenseOutside'
							);
						} else if (image.documentType === EasyClaimDocumentType.ITV) {
							itv = documentService.buildImageShell(caseId, image, 'itv');
						} else if (image.documentType === EasyClaimDocumentType.DetailPicture) {
							let componentId = 'closeUpDamageImage';

							if (damageImageNo === 0) {
								componentId = 'closeUpDamageImage';
							} else if (damageImageNo === 1) {
								componentId = 'overallDamageImage';
							} else {
								componentId = `additionalImage-${damageImageNo - 2}`;
							}

							const x = documentService.buildImageShell(caseId, image, componentId);

							if (damageImageNo === 0) {
								closeUpDamageImage = x;
							} else if (damageImageNo === 1) {
								overallDamageImage = x;
							} else {
								additionalDamageImages.push(x);
							}

							damageImageNo++;
						} else if (image.documentType === EasyClaimDocumentType.Additional) {
							let componentId = 'additionalDocumentImage';

							if (damageImageNo === 0) {
								componentId = 'additionalDocumentImage';
							} else {
								componentId = `additionalDocumentImage-${additionalDocumentNo - 1}`;
							}

							const x = documentService.buildImageShell(caseId, image, componentId);

							if (additionalDocumentNo === 0) {
								additionalDocumentImage = x;
							} else {
								additionalDocumentImages.push(x);
							}

							additionalDocumentNo++;
						}
					} else {
						reportWarning('Case has empty documentId', { caseId, index });
					}
				}
			}

			const t = this.state.theme;

			if (easyClaimCase && easyClaimCase._configuration) {
				t.colors.primary = easyClaimCase._configuration.primaryColor || t.colors.primary;
				t.colors.secondary = easyClaimCase._configuration.secondaryColor || t.colors.secondary;
				t.colors.text.primary =
					easyClaimCase._configuration.primaryTextColor || t.colors.text.primary;
				t.colors.text.secondary =
					easyClaimCase._configuration.secondaryTextColor || t.colors.text.secondary;
				t.colors.icon.primary =
					easyClaimCase._configuration.iconPrimaryColor || t.colors.icon.primary;
				t.colors.icon.secondary =
					easyClaimCase._configuration.iconSecondaryColor || t.colors.icon.secondary;
				t.colors.icon.active.primary =
					easyClaimCase._configuration.iconActivePrimaryColor || t.colors.icon.active.primary;
				t.colors.icon.active.secondary =
					easyClaimCase._configuration.iconActiveSecondaryColor || t.colors.icon.active.secondary;
				t.colors.landing.primary =
					easyClaimCase._configuration.landingPrimaryColor || t.colors.landing.primary;
				t.colors.landing.text.primary =
					easyClaimCase._configuration.landingPrimaryTextColor || t.colors.landing.text.primary;
				t.colors.landing.text.secondary =
					easyClaimCase._configuration.landingSecondaryTextColor || t.colors.landing.text.secondary;
				t.colors.landing.icon.primary =
					easyClaimCase._configuration.landingIconPrimaryColor || t.colors.landing.icon.primary;
				t.colors.landing.icon.secondary =
					easyClaimCase._configuration.landingIconSecondaryColor || t.colors.landing.icon.secondary;
				t.colors.landing.hideBackgroundGradient =
					easyClaimCase._configuration.landingHideBackgroundGradient ||
					t.colors.landing.hideBackgroundGradient;
				t.colors.success = easyClaimCase._configuration.successColor || t.colors.success;
				t.colors.info = easyClaimCase._configuration.infoColor || t.colors.info;
				t.colors.warning = easyClaimCase._configuration.warningColor || t.colors.warning;
				t.colors.danger = easyClaimCase._configuration.dangerColor || t.colors.danger;
			}
			const allowDownloadCalculationFile =
				easyClaimCase?._configuration?.allowDownloadCalculationFile === true &&
				this.state.appStateViews.some(
					(asv) =>
						asv.appState === EasyClaimAppState.Approval ||
						asv.appState === EasyClaimAppState.CashSettlementOffer
				);
			const allowDownloadCalculationFileOnFinishedView =
				easyClaimCase?._configuration?.allowDownloadCalculationFileOnFinishedView === true &&
				this.state.appStateViews.some((asv) => asv.appState === EasyClaimAppState.Finished);
			const shouldLoadEasyClaimCalculation =
				allowDownloadCalculationFileOnFinishedView || allowDownloadCalculationFile;

			const easyClaimCalculationResult = shouldLoadEasyClaimCalculation
				? await easyClaimService.getCalculation(easyClaimCase.caseId)
				: undefined;

			if (
				!!easyClaimCase._configuration?.additionalStyleFileName &&
				easyClaimCase._configuration?.additionalStyleFileName.length > 0
			) {
				// @ts-ignore
				const file = `${process.env.PUBLIC_URL}/`;
				const href = file + easyClaimCase._configuration?.additionalStyleFileName;

				document.head.querySelector<HTMLLinkElement>('link[data-easyclaim-style]')?.remove();

				const link = document.createElement('link');

				link.href = href;
				link.type = 'text/css';
				link.rel = 'stylesheet';
				link.media = 'screen,print';
				link.dataset.easyClaimStyle = '1';

				document.head.appendChild(link);
			}

			const isBlockedByWrongDevice =
				!!easyClaimCase._configuration?.doNotAllowDesktop &&
				this.state.appParameters.deviceType === DeviceType.desktop;

			if (isBlockedByWrongDevice) {
				await easyClaimService.saveBlockedByWrongDeviceEvent(
					easyClaimCase.caseId,
					this.state.appParameters.userAgentValue
				);
			}

			const isDashboardMileageTextInputCompleted =
				easyClaimCase._configuration?.frontendSteps
					?.filter((f) => f.controlsConfigurations?.length)
					?.map((f) => f.controlsConfigurations)
					?.reduce((pv, cv) => (cv && cv.length ? pv?.concat(cv) : pv), [])
					?.some((c) => c.id === 'dashboardMileageTextInput' && !!c.value) || false;

			// Resume the customer journey from where it was left off (e.g. after a
			// page reload) instead of restarting from the backend's own screen,
			// which is not always updated on purely client-side navigation.
			const storedProgress: CaseProgress | undefined = easyClaimCase.caseId
				? useAppProgressStore.getState().getProgress(easyClaimCase.caseId)
				: undefined;
			const stepOrder = easyClaimCase._configuration?.frontendSteps?.map((s) => s.appState);
			const resumedScreen = resolveResumeScreen(
				easyClaimCase.state,
				storedProgress?.screen,
				stepOrder
			);

			// Avoid re-asking for fullscreen after a reload if the customer already
			// dismissed/handled the prompt earlier in this case's journey.
			this.fullscreenPromptShown = storedProgress?.fullscreenPromptShown ?? false;

			this.setState({
				isBlockedByWrongDevice,
				easyClaimCase,
				easyClaimService,
				easyClaimCalculationResult: easyClaimCalculationResult,
				screen: resumedScreen,
				reviewReturnScreen: storedProgress?.reviewReturnScreen,
				isDashboardMileageSkipped: storedProgress?.isDashboardMileageSkipped ?? false,
				isAdditionalDocumentsSkipped: storedProgress?.isAdditionalDocumentsSkipped ?? false,
				isVinPictureSkipped: storedProgress?.isVinPictureSkipped ?? false,
				isRegistrationDocumentSkipped: storedProgress?.isRegistrationDocumentSkipped ?? false,
				isVehicleInformationSkipped: storedProgress?.isVehicleInformationSkipped ?? false,
				isDamageImagesSkipped: storedProgress?.isDamageImagesSkipped ?? false,
				damageImagesToggle: storedProgress?.damageImagesToggle,
				isVehicleImagesSkipped: storedProgress?.isVehicleImagesSkipped ?? false,
				isDamageDescriptionSkipped: storedProgress?.isDamageDescriptionSkipped ?? false,
				isOdometerAndMileageScreenCompleated:
					storedProgress?.isOdometerAndMileageScreenCompleated ?? false,
				isTermsAccepted: storedProgress?.isTermsAccepted,
				cjQuestions: storedProgress?.cjQuestions ?? [],
				cjQuestionsModified: storedProgress?.cjQuestionsModified ?? false,
				theme: t,
				registrationDocument,
				registrationDocument2,
				vehicleImageFrontLeft,
				vehicleImageFrontRight,
				vehicleImageRearRight,
				vehicleImageRearLeft,
				vehicleImageFront,
				vehicleImageRear,
				vehicleImageLeft,
				vehicleImageRight,
				dashboardImage,
				vinImage,
				overallDashboardImage,
				windshieldImage,
				closeUpDamageImage,
				overallDamageImage,
				additionalDamageImages,
				additionalDocumentImage,
				additionalDocumentImages,
				isDashboardMileageTextInputCompleted:
					isDashboardMileageTextInputCompleted ||
					(storedProgress?.isDashboardMileageTextInputCompleted ?? false),
				vehicleDatasheetInside,
				vehicleDatasheetOutside,
				dniInside,
				dniOutside,
				driverLicenseInside,
				driverLicenseOutside,
				itv,
			});
		} else {
			throw new Error('');
			//this.setState({});
		}
	}

	private showMainMenu = async () => {
		await this.setStateAsync({
			...this.state,
			isMainMenuOpen: true,
		});
	};

	private hideMainMenu = async () => {
		await this.setStateAsync({
			...this.state,
			isMainMenuOpen: false,
		});
	};

	private showHelpModal = async () => {
		await this.setStateAsync({
			...this.state,
			isHelpOpen: true,
		});
	};

	private hideHelpModal = async () => {
		await this.setStateAsync({
			...this.state,
			isHelpOpen: false,
		});
	};

	private showTutorialModal = async () => {
		await this.setStateAsync({
			...this.state,
			isHelpOpen: false,
			isTutorialOpen: true,
		});
	};

	private hideTutorialModal = async () => {
		await this.setStateAsync({
			...this.state,
			isTutorialOpen: false,
		});
	};

	private hideApiErrorModal = async () => {
		await this.setStateAsync({
			...this.state,
			serviceErrors: _.initial(this.state.serviceErrors),
		});
	};

	private showChangeLangModal = async () => {
		await this.setStateAsync({
			...this.state,
			isLangModalOpen: true,
		});
	};

	private hideChangeLangModal = async () => {
		await this.setStateAsync({
			...this.state,
			isLangModalOpen: false,
		});
	};

	private additionalDamagesDetected = (): boolean => {
		return !!this.state.easyClaimCase?._configuration?.useSolvdStyles ||
			this.state.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim() ===
				'mondialgr'
			? false
			: ImageSelectionService.imageHasValidationResult(
					this.state.vehicleImageRearLeft,
					this.state.vehicleImageRearRight,
					this.state.vehicleImageFrontLeft,
					this.state.vehicleImageFrontRight,
					this.state.vehicleImageFront,
					this.state.vehicleImageRear,
					this.state.vehicleImageLeft,
					this.state.vehicleImageRight,
					this.state.windshieldImage
				);
	};

	private hideCompleteModal = async () => {
		const newState: AppState = {
			...this.state,
			completeModalOpen: false,
			completeModalItems: [],
		};

		await this.setStateAsync(newState);
	};

	private showMismatchModal = async (images: Image[]) => {
		const newState: AppState = {
			...this.state,
			mismatchModalOpen: true,
			mismatchModalImages: images,
		};

		await this.setStateAsync(newState);
	};

	private hideMismatchModal = async () => {
		const newState: AppState = {
			...this.state,
			mismatchModalOpen: false,
			mismatchModalImages: [],
		};

		await this.setStateAsync(newState);
	};

	private showTutorial = async () => {
		const newState: AppState = {
			...this.state,
			isTutorialOpen: true,
		};

		await this.setStateAsync(newState);
	};

	private tryGoToPrevStep = (): Promise<void> => {
		this.appNavigator.onPrev();

		return Promise.resolve();
	};

	public tryGoToNextStep = async () => {
		try {
			const screenRenderer = this.state.appService.getCurrentStateScreenRenderer();
			const canScrollToMissingPhoto =
				this.state.easyClaimCase?._configuration?.canScrollToMissingPhoto;

			if (canScrollToMissingPhoto && screenRenderer) {
				if (screenRenderer.canMoveNext && screenRenderer.canMoveNext(this.state)) {
					if (
						!this.state.easyClaimCase?._configuration?.showUpdatedVerificationResults &&
						screenRenderer.hasAnyWarnings &&
						screenRenderer.hasAnyWarnings(this.state)
					) {
						if (screenRenderer.showMismatchModal) {
							if (canScrollToMissingPhoto) {
								this.scrollToFirstInvalidImage();

								return;
							}
							await screenRenderer.showMismatchModal(this.state);
						} else {
							reportError(`showMismatchModal not set for ${screenRenderer.appState}`, {
								appState: screenRenderer.appState,
								context: 'App.tryGoToNextStep',
							});
						}
					} else {
						if (!this.state.isExecutingAction) {
							await this.setStateAsync({ ...this.state, inputError: false });
							if (screenRenderer.onBeforeNext) {
								await screenRenderer.onBeforeNext(this.state);
							}
							await this.appNavigator.onNext();
							this.showFullscreenPromptOnce();
						} else {
							reportWarning(
								'Action navigation was requested while an action was already executing',
								{
									appState: screenRenderer.appState,
									isExecutingAction: this.state.isExecutingAction,
								}
							);
						}
					}
				} else {
					await this.setStateAsync({
						...this.state,
						inputError: true,
					});
					this.scrollToFirstInvalidImage();
				}
			} else if (screenRenderer) {
				if (screenRenderer.canMoveNext && screenRenderer.canMoveNext(this.state)) {
					if (
						!this.state.easyClaimCase?._configuration?.showUpdatedVerificationResults &&
						screenRenderer.hasAnyWarnings &&
						screenRenderer.hasAnyWarnings(this.state) &&
						screenRenderer.showMismatchModal
					) {
						await screenRenderer.showMismatchModal(this.state);
					} else {
						if (!this.state.isExecutingAction) {
							if (screenRenderer.onBeforeNext) {
								await screenRenderer.onBeforeNext(this.state);
							}
							await this.appNavigator.onNext();
							this.showFullscreenPromptOnce();
						} else {
							reportWarning(
								'Action navigation was requested while an action was already executing',
								{
									appState: screenRenderer.appState,
									isExecutingAction: this.state.isExecutingAction,
								}
							);
						}
					}
				} else {
					if (this.state.easyClaimCase?._configuration?.showUpdatedVerificationResults) {
						await this.appNavigator.onNext();
						this.showFullscreenPromptOnce();

						return;
					}
					if (screenRenderer.showCompleteModal) {
						await screenRenderer.showCompleteModal(this.state);
					} else {
						reportError(`showCompleteModal not set for ${screenRenderer.appState}`, {
							appState: screenRenderer.appState,
							context: 'App.tryGoToNextStep',
						});
					}
				}
			} else {
				reportError('could not get screen renderer', {
					context: 'App.tryGoToNextStep',
					screen: this.state.screen,
				});
			}
		} catch (e) {
			reportError(e, { context: 'App.tryGoToNextStep' });
			throw e;
		}
	};

	private scrollToFirstInvalidImage = () => {
		requestAnimationFrame(() => {
			const allContainers = document.querySelectorAll('.select-image-container');
			let firstEmpty: Element | null = null;

			for (let i = 0; i < allContainers.length; i++) {
				const container = allContainers[i];
				const photo = container.querySelector('.select-image-photo');

				if (photo) {
					const hasUploadedImage = photo.querySelector('img');

					if (!hasUploadedImage) {
						if (!firstEmpty) {
							firstEmpty = container;
						}
					}
				}
			}

			const mileageContainer = document.querySelector('.mileage-container-original');
			const mileageInput = mileageContainer?.querySelector(
				'.input-container input:placeholder-shown'
			) as HTMLInputElement | null;
			const isPlaceholderVisible = !!mileageInput;

			const candidates: Element[] = [];

			if (firstEmpty) {
				candidates.push(firstEmpty);
			}

			if (isPlaceholderVisible && mileageContainer) {
				const mileageScrollTarget: Element = mileageInput ?? mileageContainer;

				candidates.push(mileageScrollTarget);
			}

			if (this.state.easyClaimCase?._configuration?.showUpdatedVerificationResults) {
				const validationContainers = document.querySelectorAll('.validation-container');

				for (let i = 0; i < validationContainers.length; i++) {
					if (!validationContainers[i].classList.contains('Correct')) {
						candidates.push(validationContainers[i]);
					}
				}
			} else {
				const imageInfoContainers = document.querySelectorAll('.ImageInfoContainer-FeedbackText');

				for (let i = 0; i < imageInfoContainers.length; i++) {
					candidates.push(imageInfoContainers[i]);
				}
			}

			let scrollTarget: Element | null = null;
			let minTop = Infinity;

			for (const candidate of candidates) {
				const rect = candidate.getBoundingClientRect();

				if (rect.top < minTop) {
					minTop = rect.top;
					scrollTarget = candidate;
				}
			}

			if (scrollTarget) {
				scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
				let parent = scrollTarget.parentElement;

				while (parent) {
					const style = window.getComputedStyle(parent);
					const overflowY = style.overflowY;

					if (overflowY === 'auto' || overflowY === 'scroll') {
						const parentRect = parent.getBoundingClientRect();
						const elementRect = scrollTarget.getBoundingClientRect();
						const scrollOffset =
							elementRect.top - parentRect.top - parent.clientHeight / 2 + parent.scrollTop;

						parent.scrollTo({ top: scrollOffset, behavior: 'smooth' });
						break;
					}
					parent = parent.parentElement;
				}
			}

			if (!scrollTarget) {
				if (!this.state.isExecutingAction) {
					this.appNavigator.onNext().catch((error) => {
						reportError(error, { context: 'App.tryGoToNextStep' });
					});
				} else {
					reportWarning('Navigation fallback was requested while an action was already executing', {
						isExecutingAction: this.state.isExecutingAction,
					});
				}
			}
		});
	};

	private skipCurrentStep = async () => {
		await this.appNavigator.onSkip(this.state);
	};

	private getFileUrl = (fileId: number): string => {
		return this.state.documentService!.getFileUrl(fileId);
	};

	private confirmMileageModal = async (insertedMileage: number | null) => {
		const currentVehicle = this.state.easyClaimCase?.vehicle;

		const newVehicle: VehicleDto = {
			...currentVehicle,
			userProvidedMileage: insertedMileage,
		};

		const frontendSteps = this.state.easyClaimCase?._configuration?.frontendSteps;

		const frontendStep = frontendSteps?.find((fs) => fs.appState === this.state.screen);

		if (!frontendStep) {
			return;
		}
		const frontendStepIndex = frontendSteps?.indexOf(frontendStep) || 0;

		const controlsConfigurations = frontendStep?.controlsConfigurations;

		const dashboardMileageTextInputConfiguration = controlsConfigurations?.find(
			(c) => c.id === 'dashboardMileageTextInput'
		);

		const validationResults = dashboardMileageTextInputConfiguration?.validationResults?.filter(
			(vr) => vr.feature !== 'MileageMustBeConsistent'
		);

		const newDashboardMileageTextInputConfiguration: ControlConfiguration = {
			...dashboardMileageTextInputConfiguration,
			validationResults: validationResults,
		};

		const newControlsConfigurations: ControlConfiguration[] =
			controlsConfigurations
				?.filter((c) => c.id !== 'dashboardMileageTextInput')
				.concat([newDashboardMileageTextInputConfiguration]) || [];

		const newFrontendStep: EasyClaimFrontendStep = {
			...frontendStep,
			controlsConfigurations: newControlsConfigurations,
		};

		// TODO !!! WRONG ORDER
		//const newFrontendSteps: EasyClaimFrontendStep[] = frontendSteps?.filter(fs => fs.appState !== this.state.screen).concat([newFrontendStep]) || [];
		const newFrontendSteps: EasyClaimFrontendStep[] =
			frontendSteps
				?.slice(0, frontendStepIndex)
				?.concat([newFrontendStep])
				?.concat(frontendSteps.slice(frontendStepIndex + 1, frontendSteps.length)) || [];

		const newConfiguration = {
			...this.state.easyClaimCase?._configuration,
			frontendSteps: newFrontendSteps,
		};

		const newEasyClaimCase: EasyClaimCaseResponseDto | undefined = {
			...this.state.easyClaimCase,
			_configuration: newConfiguration,
			vehicle: newVehicle,
		};

		const newState = {
			...this.state,
			easyClaimCase: newEasyClaimCase,
			isMileageMismatchModalConfirmValue: true,
			isMileageMismatchModalOpen: false,
		};

		await this.setStateAsync(newState);
	};

	private reverseMileageModal = async () => {
		//const mileage = this.getDashboardImageMileage();
		const mileage = this.state.appService.getDashboardImageMileageValue();

		if (mileage === null) {
			reportWarning('Cannot reverse mileage when mileage is unavailable');

			return;
		}
		const result = await this.state.appService.updateMileageOnCase(mileage);

		// TODOKG: Check if this does anything
		// const validationResults = result?.validationResults || [];
		const validationResults = [];

		const frontendSteps = this.state.easyClaimCase?._configuration?.frontendSteps;

		const frontendStep = frontendSteps?.find((fs) => fs.appState === this.state.screen);

		if (!frontendStep) {
			return;
		}
		const frontendStepIndex = frontendSteps?.indexOf(frontendStep) || 0;

		const controlsConfigurations = frontendStep?.controlsConfigurations;

		const dashboardMileageTextInputConfiguration = controlsConfigurations?.find(
			(c) => c.id === 'dashboardMileageTextInput'
		);

		const newDashboardMileageTextInputConfiguration: ControlConfiguration = {
			...dashboardMileageTextInputConfiguration,
			validationResults: validationResults,
		};

		const newControlsConfigurations: ControlConfiguration[] =
			controlsConfigurations
				?.filter((c) => c.id !== 'dashboardMileageTextInput')
				.concat([newDashboardMileageTextInputConfiguration]) || [];

		const newFrontendStep: EasyClaimFrontendStep = {
			...frontendStep,
			controlsConfigurations: newControlsConfigurations,
		};

		// TODO !!! WRONG ORDER
		///const newFrontendSteps: EasyClaimFrontendStep[] = frontendSteps?.filter(fs => fs.appState !== this.state.screen).concat([newFrontendStep]) || [];
		const newFrontendSteps: EasyClaimFrontendStep[] =
			frontendSteps
				?.slice(0, frontendStepIndex)
				?.concat([newFrontendStep])
				?.concat(frontendSteps.slice(frontendStepIndex + 1, frontendSteps.length)) || [];

		const newConfiguration = {
			...this.state.easyClaimCase?._configuration,
			frontendSteps: newFrontendSteps,
		};

		const currentVehicle = this.state.easyClaimCase?.vehicle;

		const newVehicle: VehicleDto = {
			...currentVehicle,
			userProvidedMileage: mileage,
		};

		const newEasyClaimCase: EasyClaimCaseResponseDto = {
			...this.state.easyClaimCase,
			_configuration: newConfiguration,
			vehicle: newVehicle,
		};

		const newAppState = {
			...this.state,
			easyClaimCase: newEasyClaimCase,
			isMileageMismatchModalConfirmValue: true,
			isMileageMismatchModalOpen: false,
		};

		await this.setStateAsync(newAppState);
	};

	private hideMileageModal = async () => {
		await this.setStateAsync({ isMileageMismatchModalOpen: false });
	};
}

const AppContainer = forwardRef((props: any, ref: Ref<HTMLDivElement>) => (
	<Div100vh ref={ref} {...props} />
));

const Header = styled.header`
	padding: 0.5rem;
	border-bottom: 1px solid ${(p) => transparentize(0.8, p.theme.colors.secondary)};
`;
